'use client';

import type { ReactNode } from 'react';
import { toast as sonnerToast, type ExternalToast } from 'sonner';

export type ToastProps = ExternalToast & {
  title?: string | ReactNode;
  variant?: 'default' | 'destructive' | 'success' | 'warning' | 'info';
};

function toast({ title = '', description, action, variant = 'default', duration, id, ...props }: ToastProps) {
  sonnerToast.dismiss();

  const options = {
    id: id,
    description: description,
    duration: duration,
    action: action,
    icon: null,
    ...props,
  };

  let toastId: string | number;

  switch (variant) {
    case 'destructive':
      toastId = sonnerToast.error(title, options);
      break;
    case 'success':
      toastId = sonnerToast.success(title, options);
      break;
    case 'warning':
      toastId = sonnerToast.warning(title, options);
      break;
    case 'info':
      toastId = sonnerToast.info(title, options);
      break;
    default:
      toastId = sonnerToast(title, options);
  }

  return {
    id: toastId.toString(),
    dismiss: () => sonnerToast.dismiss(toastId),
    update: (newProps: Partial<ToastProps>) => {
      // Dismiss the current toast and create a new one with updated props
      sonnerToast.dismiss(toastId);
      return toast({ ...props, ...newProps });
    },
  };
}

function useToast() {
  return {
    toast,
    dismiss: (toastId?: string) => {
      if (toastId) {
        sonnerToast.dismiss(toastId);
      } else {
        sonnerToast.dismiss();
      }
    },
    // For backward compatibility, return empty toasts array
    toasts: [],
  };
}

export { useToast, toast };
